import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import Input from "../../../../../../components/Form/Input/input";
import { Form, FormGroup } from "../../../../../../components/Form/styles";
import { Box } from "../../../../../../components/Layout";
import SaveBar from "../../../../../../components/SaveBar";
import { SubHeader } from "../../../../../../components/SubHeader";
import { ItemSectionLinks } from "../../../../../../model/site";
import { clone, deepEqual } from "../../../../../../utils/objects";
import { sanitizeUrl } from "../../../../../../utils/url";
import { uuidv4 } from "../../../../../../utils/uuid";
import { ItemThumbnail, type ThumbnailType } from "../../../../components/ItemThumbnail";
import { PageLinkField, useLinkablePageId } from "../../../../components/PageLinkField";
import { useItemPreview } from "../../../../hooks/Section";
import { navigationState } from "../../../Sections/navigationState";
import { LinksNavigationState } from "../../linksNavigationState";
import { Container, ThumbnailBox } from "./styles";

const ItemLinkPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const linksNavigationState = navigationState.get() as LinksNavigationState;

  const headerTitle = useRef(
    linksNavigationState.editingLink?.id
      ? t("pages.editor.links.item.header.title.edit")
      : t("pages.editor.links.item.header.title.add")
  );

  const initialItemState = useRef<ItemSectionLinks>(
    clone<ItemSectionLinks>(
      linksNavigationState.editingLink || {
        id: uuidv4(),
      }
    )
  );

  const [item, setItem] = useState(initialItemState.current);

  useItemPreview(linksNavigationState.currentSection, item, existing => existing.id === item.id);

  // a page of the Keepo (pageId) or an address (link); a page that's gone (or not PLUS) needs an address again
  const linkedPageId = useLinkablePageId(item.pageId);
  const isSaveDisabled = !item.title || (!linkedPageId && !item.link);

  const hasUnsavedChanges = () => !deepEqual(initialItemState.current, item);

  const onThumbnailChange = (thumbnailType: ThumbnailType) => {
    setItem({
      ...item,
      thumbnailType,
      image: thumbnailType === "IMAGE" ? item.image : undefined,
      emoji: thumbnailType === "EMOJI" ? item.emoji : undefined,
    });
  };

  const saveHandler = () => {
    const items = linksNavigationState.currentSection.items || [];
    const itemIndex = items.findIndex(({ id }) => id === item.id);

    if (itemIndex >= 0) {
      items[itemIndex] = {
        ...item,
        pageId: linkedPageId,
        link: linkedPageId ? "" : (sanitizeUrl(item.link) as string),
        thumbnailType: item.thumbnailType || "IMAGE",
      };
    } else {
      items.push({
        ...item,
        pageId: linkedPageId,
        link: linkedPageId ? "" : (sanitizeUrl(item.link) as string),
        thumbnailType: item.thumbnailType || "IMAGE",
      });
    }

    linksNavigationState.currentSection.items = items;
    navigate(-1);
  };

  return (
    <Container>
      <SubHeader
        title={headerTitle.current}
        crumbs={[
          { label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" },
          { label: t("pages.editor.links.header.title"), to: "/editor/links" },
        ]}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      <Box>
        <Form>
          <FormGroup>
            <Input
              id="title"
              required
              type="text"
              value={item.title}
              autoCapitalize={"on"}
              maxLength={56}
              onChange={e => setItem({ ...item, title: e.target.value })}
              label={t("pages.editor.links.item.form.title")}
            />
          </FormGroup>
          <PageLinkField pageId={item.pageId} onChange={pageId => setItem({ ...item, pageId })} />
          {!linkedPageId && (
            <FormGroup>
              <Input
                id="link"
                required
                type="link"
                autoCapitalize="none"
                placeholder="https://"
                value={item.link}
                onChange={e => setItem({ ...item, link: e.target.value })}
                label={t("pages.editor.links.item.form.link")}
              />
            </FormGroup>
          )}
        </Form>
      </Box>
      <ThumbnailBox borderBottom>
        <ItemThumbnail
          types={["IMAGE", "EMOJI", "NONE"]}
          thumbnailType={item.thumbnailType || "IMAGE"}
          onThumbnailChange={onThumbnailChange}
          image={item.image}
          emoji={item.emoji}
          onChangeEmoji={emoji => setItem({ ...item, emoji })}
          onChangeImage={image => setItem({ ...item, image })}
        />
      </ThumbnailBox>
      <SaveBar onSave={saveHandler} disabled={isSaveDisabled} hasUnsavedChanges={hasUnsavedChanges} />
    </Container>
  );
};

export default ItemLinkPage;
